/**
 * ============================================================================
 * TREND TREE — GIFT WITH PURCHASE (GWP) CONFIGURATION
 * ============================================================================
 * File:      assets/gift-mapping.js
 * Purpose:   Single source of truth for the automatic complimentary gift
 *            system. Contains progress-bar milestones, the eligibility
 *            threshold, gift-product registry, and the rule engine that
 *            decides which free gifts a cart qualifies for.
 *
 * IMPORTANT — This file is CONFIG ONLY. It has zero DOM / Cart API code.
 * All cart reading/writing logic lives in assets/cart-gift-handler.js,
 * which imports (via a plain <script> load order) the objects below.
 *
 * ----------------------------------------------------------------------------
 * HOW TO EDIT THIS WITHOUT TOUCHING LOGIC
 * ----------------------------------------------------------------------------
 * 1. To change milestone amounts/labels          -> edit MILESTONES
 * 2. To change the free-gift unlock threshold     -> edit GIFT_THRESHOLD
 * 3. To register a new gift product               -> edit GIFT_PRODUCTS
 * 4. To add/change a trigger rule                 -> edit RULES
 * No other file needs to change for config-only updates.
 *
 * ----------------------------------------------------------------------------
 * REQUIRED SHOPIFY ADMIN TAGGING (apply these tags to PRODUCTS, not variants)
 * ----------------------------------------------------------------------------
 * TRIGGER products (things a customer buys that unlock a gift):
 *   gift-type:champi-oil       -> Champi Oil products
 *   gift-type:bhringraj-oil    -> Bhringraj Oil products
 *   gift-type:almond-oil       -> Almond Oil products
 *   gift-type:jojoba-oil       -> Jojoba Oil products
 *   gift-type:hair-oil         -> GENERIC tag — apply to EVERY hair oil
 *                                  product (Champi, Bhringraj, Almond,
 *                                  Jojoba, and any future oil) IN ADDITION
 *                                  to its specific oil tag above. This is
 *                                  what lets Rule 7 ("any hair oil") work
 *                                  without hardcoding oil names.
 *   gift-type:face-product     -> Neem Face Wash, Sleeping Mask,
 *                                  Radiance Mask, No Tan Mask (already
 *                                  applied — confirmed on Radiance Clay Mask)
 *   gift-type:shampoo          -> any Shampoo product
 *   gift-type:conditioner      -> any Conditioner product
 *   gift-type:hair-mask        -> any Hair Mask product
 *
 * GIFT products (the free items themselves — tag the gift product page):
 *   gift-product:neem-detangling-comb
 *   gift-product:jade-roller
 *   gift-product:facial-head-band
 *   gift-product:hair-wrap
 *   gift-product:neem-shampoo-comb
 *
 * The handler resolves gift product/variant IDs by looking up these tags
 * once (cached), so you never hardcode a product or variant ID in code.
 * ============================================================================
 */

(function (window) {
  "use strict";

  /**
   * --------------------------------------------------------------------
   * 1. PROGRESS BAR MILESTONES (display-only — do not add products)
   * --------------------------------------------------------------------
   * amount: rupees (matches cart subtotal comparison, see handler)
   * key:    stable identifier used for icon/DOM targeting
   * icon:   maps to an icon element already in cart-gift-progress-bar.liquid
   */
  var MILESTONES = [
    { key: "travel-ritual", amount: 999, label: "Complimentary Travel Ritual", icon: "travel" },
    { key: "free-delivery", amount: 1299, label: "Complimentary Delivery", icon: "delivery" },
    { key: "ritual-companion", amount: 2099, label: "Complimentary Ritual Companion", icon: "companion" }
  ];

  /**
   * --------------------------------------------------------------------
   * 2. GIFT UNLOCK THRESHOLD
   * --------------------------------------------------------------------
   * Product-mapping gifts (Section 3/4 below) ONLY activate at or above
   * this subtotal. Below this, gifts are stripped from the cart even if
   * their trigger products are still present. Progress-bar milestones
   * above still display independently of this.
   *
   * NOTE: previously this matched the old top milestone (2499). It has
   * been updated to match the new top milestone (2099) below. If gift
   * unlock is meant to stay independent of the top progress-bar
   * milestone, change this back to whatever value you actually want.
   */
  var GIFT_THRESHOLD = 2099;

  /**
   * --------------------------------------------------------------------
   * 3. GIFT PRODUCT REGISTRY
   * --------------------------------------------------------------------
   * Every free gift the system can add. "tag" is the product tag on the
   * GIFT product itself, used to resolve its variant ID at runtime.
   * "key" is the internal id referenced by RULES below and stamped onto
   * the cart line item property `_gift_type`.
   */
  var GIFT_PRODUCTS = {
    "neem-detangling-comb": {
      key: "neem-detangling-comb",
      tag: "gift-product:neem-detangling-comb",
      label: "Neem Detangling Comb"
    },
    "jade-roller": {
      key: "jade-roller",
      tag: "gift-product:jade-roller",
      label: "Jade Roller"
    },
    "facial-head-band": {
      key: "facial-head-band",
      tag: "gift-product:facial-head-band",
      label: "Facial Head Band"
    },
    "hair-wrap": {
      key: "hair-wrap",
      tag: "gift-product:hair-wrap",
      label: "Hair Wrap"
    },
    "neem-shampoo-comb": {
      key: "neem-shampoo-comb",
      tag: "gift-product:neem-shampoo-comb",
      label: "Neem Shampoo Comb"
    }
  };

  /**
   * --------------------------------------------------------------------
   * 4b. GIFT CONFLICT GROUPS
   * --------------------------------------------------------------------
   * Some gifts shouldn't both appear in the same cart even if multiple
   * independent rules technically matched — e.g. a customer should never
   * receive TWO different combs. Each group lists gift keys in priority
   * order; if more than one member of a group is resolved, only the
   * highest-priority one is kept.
   *
   * Example: Champi Oil (Rule 1 -> Detangling Comb) + any Shampoo
   * (Rule 4 -> Shampoo Comb) both matching at once would otherwise give
   * two combs. This group keeps only the Shampoo Comb in that case.
   */
  var CONFLICT_GROUPS = [
    {
      group: "comb",
      priority: ["neem-shampoo-comb", "neem-detangling-comb"]
    }
  ];

  /**
   * --------------------------------------------------------------------
   * 5. RULE ENGINE
   * --------------------------------------------------------------------
   * Each rule is evaluated independently against the set of gift-type
   * tags present in the cart's NON-GIFT line items. Rules are ADDITIVE:
   * every rule whose condition is met contributes its gift keys to one
   * combined Set, so a cart can satisfy multiple rules at once and get
   * the union of all resulting gifts (duplicates automatically collapse
   * because gift keys are deduplicated in a Set — see resolveGiftsForTags
   * in the handler).
   *
   * match.any -> at least ONE of these tags must be present
   * match.all -> EVERY one of these tags must be present
   * (a rule can use one or the other, not both)
   *
   * Order does not affect output (rules don't short-circuit each other),
   * but is kept in spec order for readability.
   */
  var RULES = [
    {
      id: "rule-1-champi-oil",
      description: "Champi Oil -> Neem Detangling Comb",
      match: { all: ["gift-type:champi-oil"] },
      gifts: ["neem-detangling-comb"]
    },
    {
      id: "rule-2-almond-or-jojoba",
      description: "Almond Oil OR Jojoba Oil -> Jade Roller",
      match: { any: ["gift-type:almond-oil", "gift-type:jojoba-oil"] },
      gifts: ["jade-roller"]
    },
    {
      id: "rule-3-face-product",
      description: "Any face product (face wash / sleeping / radiance / no tan mask) -> Facial Head Band",
      match: { any: ["gift-type:face-product"] },
      gifts: ["facial-head-band"]
    },
    {
      id: "rule-4-shampoo",
      description: "Any Shampoo -> Neem Shampoo Comb",
      match: { any: ["gift-type:shampoo"] },
      gifts: ["neem-shampoo-comb"]
    },
    {
      id: "rule-5-hairmask-or-conditioner",
      description: "Any Hair Mask OR Any Conditioner -> Hair Wrap",
      match: { any: ["gift-type:hair-mask", "gift-type:conditioner"] },
      gifts: ["hair-wrap"]
    },
    {
      id: "rule-6-shampoo-plus-conditioner",
      description: "Shampoo + Conditioner -> Hair Wrap + Neem Shampoo Comb",
      match: { all: ["gift-type:shampoo", "gift-type:conditioner"] },
      gifts: ["hair-wrap", "neem-shampoo-comb"]
    },
    {
      id: "rule-7-shampoo-plus-conditioner-plus-oil",
      description: "Shampoo + Conditioner + Any Hair Oil -> Hair Wrap + Neem Detangling Comb",
      match: { all: ["gift-type:shampoo", "gift-type:conditioner", "gift-type:hair-oil"] },
      gifts: ["hair-wrap", "neem-detangling-comb"]
    },
    {
      id: "rule-8-bhringraj-oil",
      description: "Bhringraj Oil -> Neem Detangling Comb",
      match: { all: ["gift-type:bhringraj-oil"] },
      gifts: ["neem-detangling-comb"]
    }
  ];

  /**
   * --------------------------------------------------------------------
   * 5. CART LINE ITEM PROPERTY KEYS
   * --------------------------------------------------------------------
   * These properties are stamped onto every gift line item when it's
   * added to the cart. They are how the handler recognizes "this line
   * is a system-managed gift" on every cart read, and how an external
   * automatic discount app (see project notes) should target 100% off.
   * Shopify hides properties prefixed with "_" from the customer-facing
   * cart UI automatically.
   */
  var LINE_ITEM_PROPS = {
    giftFlagKey: "_auto_gift", // "true" on every system-added gift line
    giftFlagValue: "true",
    giftTypeKey: "_gift_type", // e.g. "neem-detangling-comb"
    giftRuleKey: "_gift_source_rule" // e.g. "rule-1-champi-oil" (debugging/audit)
  };

  /**
   * --------------------------------------------------------------------
   * 6. TAG PREFIXES
   * --------------------------------------------------------------------
   */
  var TAG_PREFIX = {
    trigger: "gift-type:",
    giftProduct: "gift-product:"
  };

  /**
   * --------------------------------------------------------------------
   * 7. GENERAL SETTINGS
   * --------------------------------------------------------------------
   */
  var SETTINGS = {
    debug: false, // set true to see rule evaluation logs in console
    maxGiftQtyPerType: 1,
    // How long (ms) resolved gift variant IDs are cached in memory before
    // re-querying the storefront, since gift products rarely change.
    variantCacheTtlMs: 5 * 60 * 1000
  };

  /**
   * --------------------------------------------------------------------
   * 8. PURE HELPER — resolveGiftsForTags
   * --------------------------------------------------------------------
   * Given a Set (or array) of gift-type tags present among the cart's
   * non-gift line items, returns an array of unique gift keys that
   * should be in the cart. Pure function, no side effects — safe to
   * unit test and safe to call as often as needed.
   *
   * @param {Set<string>|string[]} presentTagSet
   * @returns {{ giftKeys: string[], matchedRules: string[] }}
   */
  function resolveGiftsForTags(presentTagSet) {
    var presentTags = presentTagSet instanceof Set ? presentTagSet : new Set(presentTagSet || []);
    var giftKeys = new Set();
    var matchedRules = [];

    RULES.forEach(function (rule) {
      var isMatch = false;

      if (rule.match.all) {
        isMatch = rule.match.all.every(function (tag) {
          return presentTags.has(tag);
        });
      } else if (rule.match.any) {
        isMatch = rule.match.any.some(function (tag) {
          return presentTags.has(tag);
        });
      }

      if (isMatch) {
        matchedRules.push(rule.id);
        rule.gifts.forEach(function (giftKey) {
          if (GIFT_PRODUCTS[giftKey]) {
            giftKeys.add(giftKey);
          } else if (SETTINGS.debug) {
            console.warn(
              "[TrendTreeGifts] Rule " + rule.id + " references unknown gift key: " + giftKey
            );
          }
        });
      }
    });

    // Resolve conflict groups (e.g. never award two different combs)
    CONFLICT_GROUPS.forEach(function (conflict) {
      var presentInGroup = conflict.priority.filter(function (key) {
        return giftKeys.has(key);
      });
      if (presentInGroup.length > 1) {
        var winner = presentInGroup[0]; // priority[0] found first wins
        conflict.priority.forEach(function (key) {
          if (key !== winner) {
            giftKeys.delete(key);
          }
        });
        if (SETTINGS.debug) {
          console.log(
            "[TrendTreeGifts] Conflict resolved in group '" +
              conflict.group +
              "': kept '" +
              winner +
              "', dropped " +
              JSON.stringify(presentInGroup.filter(function (k) { return k !== winner; }))
          );
        }
      }
    });

    if (SETTINGS.debug) {
      console.log("[TrendTreeGifts] Matched rules:", matchedRules);
      console.log("[TrendTreeGifts] Resolved gift keys:", Array.from(giftKeys));
    }

    return {
      giftKeys: Array.from(giftKeys),
      matchedRules: matchedRules
    };
  }

  /**
   * --------------------------------------------------------------------
   * 9. PURE HELPER — getNextMilestone
   * --------------------------------------------------------------------
   * Given a gift-excluded subtotal (in rupees), returns the next
   * unreached milestone and how much more is needed, or null if all
   * milestones are already unlocked.
   *
   * @param {number} subtotal
   * @returns {{ milestone: object, amountRemaining: number } | null}
   */
  function getNextMilestone(subtotal) {
    for (var i = 0; i < MILESTONES.length; i++) {
      if (subtotal < MILESTONES[i].amount) {
        return {
          milestone: MILESTONES[i],
          amountRemaining: MILESTONES[i].amount - subtotal
        };
      }
    }
    return null;
  }

  /**
   * --------------------------------------------------------------------
   * 10. PUBLIC EXPO
   * --------------------------------------------------------------------
   * Exposed as a single global namespace so cart-gift-handler.js and
   * the Liquid snippets' inline scripts can read it without a bundler.
   */
  window.TrendTreeGifts = window.TrendTreeGifts || {};
  window.TrendTreeGifts.config = {
    MILESTONES: MILESTONES,
    GIFT_THRESHOLD: GIFT_THRESHOLD,
    GIFT_PRODUCTS: GIFT_PRODUCTS,
    RULES: RULES,
    LINE_ITEM_PROPS: LINE_ITEM_PROPS,
    TAG_PREFIX: TAG_PREFIX,
    SETTINGS: SETTINGS
  };
  window.TrendTreeGifts.resolveGiftsForTags = resolveGiftsForTags;
  window.TrendTreeGifts.getNextMilestone = getNextMilestone;
})(window);
